import React, { useContext, useEffect, useState } from 'react';

import { Icon } from 'design-system-web';
import {
  ChannelSelectorButton,
  ControlMenu,
  ChannelCircleListView,
  Button,
  Stepper,
  ErrorMessage,
  ToggleView,
  ChannelProfileEdit,
} from 'components';
import { history } from 'helpers';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getClient } from 'lane-shared/apollo';
import { routes } from 'lane-shared/config';
import { UserDataContext } from 'lane-shared/contexts';
import { addChannelRelationship } from 'lane-shared/graphql/mutation';
import { castGraphQLObject, getDisplayName } from 'lane-shared/helpers';
import { pause } from 'async-timing';
import { cloneChannel } from 'lane-shared/helpers/channel';
import { AddressType } from 'lane-shared/types/AddressType';
import {
  ChannelExperienceTypeEnum,
  ActiveChannelTypeEnum,
} from 'constants-channel';

import { ChannelSearchHierarchiesEnum } from 'components/lane/ChannelSearch';
import { ChannelSelectorModesEnum } from 'components/lane/ChannelSelector';
import ChannelInfoEdit from 'components/lane/ChannelSettingsEdit/ChannelInfoEdit';
import { AdminPage } from 'components/layout';
import { H3, H4, H5, M, S } from 'components/typography';

import getChannelQuery from './getChannelQuery';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { SuitesFields } from 'components/lane/SuitesMultiSelectDropdown';
import {
  ChannelRelationshipInput,
  ChannelRelationshipType,
  AddChannelRelationshipMutation,
  AddChannelRelationshipMutationVariables,
  GetChannelQuery,
} from 'graphql-query-contracts';

import styles from './styles.scss';
import { Channel } from 'packages/lane-shared/types/ChannelType';

const allowedParentHierarchies = [ChannelSearchHierarchiesEnum.Parent];
const searchKey = 'NewChannelAndRelationshipChannelSelectorButton';
const STEPS = [
  'web.pages.portal.admin.channel.relationships.new.index.steps.selectParent',
  'web.pages.portal.admin.channel.relationships.new.index.steps.selectSetup',
  'web.pages.portal.admin.channel.relationships.new.index.steps.create',
];

const allowedTypes = [
  ActiveChannelTypeEnum.Company,
  ActiveChannelTypeEnum.Service,
  ActiveChannelTypeEnum.Restaurant,
  ActiveChannelTypeEnum.Retail,
  ActiveChannelTypeEnum.Entertainment,
];

const TRANSLATION_KEYS = {
  stepOne: 'web.pages.portal.admin.channel.relationships.new.index.stepOne',
  stepOneDescription:
    'web.pages.portal.admin.channel.relationships.new.index.stepOneDescription',
  stepTwo: 'web.pages.portal.admin.channel.relationships.new.index.stepTwo',
  stepTwoDescription:
    'web.pages.portal.admin.channel.relationships.new.index.stepTwoDescription',
  stepThree: 'web.pages.portal.admin.channel.relationships.new.index.stepThree',
  stepThreeEditNewChannelInfo:
    'web.pages.portal.admin.channel.relationships.new.index.stepThreeEditNewChannelInfo',
  stepThreeEditNewChannelProfile:
    'web.pages.portal.admin.channel.relationships.new.index.stepThreeEditNewChannelProfile',
  nextButton:
    'web.pages.portal.admin.channel.relationships.new.index.nextButton',
  backButton:
    'web.pages.portal.admin.channel.relationships.new.index.backButton',
  createButton:
    'web.pages.portal.admin.channel.relationships.new.index.createButton',
  getChannelErrorTitle:
    'web.pages.portal.admin.channel.relationships.new.index.getChannelError.title',
  getChannelErrorMessage:
    'web.pages.portal.admin.channel.relationships.new.index.getChannelError.message',
  getChannelAddressError:
    'web.pages.portal.admin.channel.relationships.new.index.getChannelAddressError',
  createChannelErrorTitle:
    'web.pages.portal.admin.channel.relationships.new.index.createChannelError.title',
  createChannelErrorMessage:
    'web.pages.portal.admin.channel.relationships.new.index.createChannelError.message',
  createChannelSuccessToast:
    'web.pages.portal.admin.channel.relationships.new.index.createChannelSuccessToast',
  summaryTitle:
    'web.pages.portal.admin.channel.relationships.new.index.summaryTitle',
  location: 'web.pages.portal.admin.channel.relationships.new.index.location',
  type: 'web.pages.portal.admin.channel.relationships.new.index.type',
  parentChannel:
    'web.pages.portal.admin.channel.relationships.new.index.parentChannel',
  copiedFrom:
    'web.pages.portal.admin.channel.relationships.new.index.copiedFrom',
  summaryNote:
    'web.pages.portal.admin.channel.relationships.new.index.summaryNote',
};

export default function NewChannelAndRelationship() {
  const { user } = useContext(UserDataContext);
  const { channel } = useChannelAdminContext();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [parent, setParent] = useState<Channel | null>(null);
  const [newChannel, setNewChannel] = useState<Channel | null>(null);
  const [copyFrom, setCopyFrom] = useState<Channel | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { t } = useTranslation();
  const [suites, setSuiteIds] = useState<SuitesFields>({ suiteIds: [] });

  const steps = STEPS.map(s => t(s));

  function updateNewChannel(props: Partial<Channel>) {
    // @ts-expect-error TS-10761 FIXME: Incompatible API Type for `Channel` expectation.
    setNewChannel(prevState => ({
      ...prevState,
      ...props,
      tags: [''],
    }));
  }

  function checkAddress(address: AddressType | null): boolean {
    return !!address?.geo?.[0];
  }

  function selectParent(channel: Channel | null) {
    setParent(channel);
  }

  useEffect(() => {
    if (parent?._id) {
      selectCopyFrom(parent);
    }
  }, [parent?._id]);

  async function selectCopyFrom(copyFrom: Channel | null) {
    let fullChannel: Channel;

    setError(null);
    setLoading(true);
    await pause();

    try {
      // TS-10761 FIXME: The channel is being cast from an API type to a common channel type
      // first get a full copy of the channel we are copying
      const { data } = await getClient().query<GetChannelQuery>({
        query: getChannelQuery,
        variables: {
          id: copyFrom?._id,
        },
      });

      // @ts-expect-error TS-10761 FIXME: Incompatible API Type for `Channel` expectation.
      fullChannel = data.channel;
      fullChannel.tags = [''];
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.getChannelErrorTitle, {
          channelName: getDisplayName(copyFrom),
        }),
        message: t(TRANSLATION_KEYS.getChannelErrorMessage),
      });

      return;
    } finally {
      setLoading(false);
    }

    setCopyFrom(fullChannel);
    setError(
      checkAddress(channel!.address!)
        ? null
        : new Error(
            t(TRANSLATION_KEYS.getChannelAddressError, {
              channelName: getDisplayName(channel),
            })
          )
    );

    // use the construct channel to make a fully formed channel object
    setNewChannel(
      cloneChannel(fullChannel!, channel!.address!, parent!, user!._id)
    );
  }

  async function doCreate() {
    const relationship: ChannelRelationshipInput = {
      channel: castGraphQLObject(newChannel, true),
      relatedTo: {
        _id: channel!._id,
      },
      type: ChannelRelationshipType.Tenant,
    };

    if (suites.suiteIds?.length) {
      relationship.suiteIds = suites?.suiteIds;
    }

    try {
      await pause();
      await getClient().mutate<
        AddChannelRelationshipMutation,
        AddChannelRelationshipMutationVariables
      >({
        mutation: addChannelRelationship,
        variables: { relationship },
      });
      window.Toast.show(
        <p>
          {t(TRANSLATION_KEYS.createChannelSuccessToast, {
            channelName: newChannel?.name,
          })}
        </p>
      );
      history.push(
        routes.channelAdminRelationships.replace(':id', channel!.slug)
      );
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.createChannelErrorTitle, {
          channelName: getDisplayName(newChannel),
        }),
        message: t(TRANSLATION_KEYS.createChannelErrorMessage),
        error: err,
      });
    }

    setLoading(false);
  }

  return (
    <AdminPage className={styles.NewChannelAndRelationship}>
      <Stepper
        className={styles.stepper}
        steps={steps}
        active={currentStep}
        maxStep={currentStep}
        onClick={step => setCurrentStep(steps.findIndex(name => name === step))}
        disabled={loading}
      />

      {currentStep === 0 && (
        <>
          <div className={styles.search}>
            <H3 mb={4} bold>
              {t(TRANSLATION_KEYS.stepOne)}
            </H3>
            <H4 mb={4}>{t(TRANSLATION_KEYS.stepOneDescription)}</H4>
            <hr />
            <ChannelSelectorButton
              storageKey={`${searchKey}parent`}
              types={allowedTypes}
              hierarchies={allowedParentHierarchies}
              modes={[ChannelSelectorModesEnum.Search]}
              onChannelSelected={selectParent}
            />

            {parent && (
              <Link
                to={`${routes.channelAdmin.replace(
                  ':id',
                  parent.slug
                )}/profile`}
                target="_blank"
              >
                <ChannelCircleListView
                  className={styles.channelListView}
                  channel={parent}
                />
              </Link>
            )}
          </div>

          <ControlMenu className={styles.menu}>
            <hr />
            <Button
              variant="contained"
              disabled={!parent}
              loading={loading}
              testId="tenantNext"
              onClick={async () => {
                setLoading(true);
                await pause(500);
                setCurrentStep(1);
                setLoading(false);
              }}
              endIcon={<Icon name="chevron-right" />}
            >
              {t(TRANSLATION_KEYS.nextButton)}
            </Button>
          </ControlMenu>
        </>
      )}

      {currentStep === 1 && parent && (
        <>
          <div className={styles.search}>
            <H3 mb={4} bold>
              {t(TRANSLATION_KEYS.stepTwo)}
            </H3>
            <H4 mb={4}>{t(TRANSLATION_KEYS.stepTwoDescription)}</H4>
            <hr data-margin-top={4} data-margin-bottom={2} />
            <ErrorMessage error={error} />
            <ChannelSelectorButton
              storageKey={`${searchKey}copyFrom`}
              types={allowedTypes}
              modes={[ChannelSelectorModesEnum.Search]}
              onChannelSelected={selectCopyFrom}
            />
            {copyFrom && (
              <Link
                to={`${routes.channelAdmin.replace(
                  ':id',
                  copyFrom.slug
                )}/profile`}
                target="_blank"
              >
                <ChannelCircleListView
                  className={styles.channelListView}
                  channel={copyFrom}
                />
              </Link>
            )}
          </div>

          <ControlMenu className={styles.menu}>
            <Button
              variant="outlined-light"
              disabled={!parent}
              testId="tenantBack"
              onClick={() => setCurrentStep(0)}
              startIcon={<Icon name="chevron-left" />}
              loading={loading}
            >
              {t(TRANSLATION_KEYS.backButton)}
            </Button>
            <hr />
            <Button
              variant="contained"
              testId="tenantNext"
              // @ts-expect-error TS-10761 FIXME: Incompatible API Type for `Channel` expectation.
              disabled={!parent || error}
              onClick={async () => {
                setLoading(true);
                await pause(500);
                setCurrentStep(2);
                setLoading(false);
              }}
              endIcon={<Icon name="chevron-right" />}
              loading={loading}
            >
              {t(TRANSLATION_KEYS.nextButton)}
            </Button>
          </ControlMenu>
        </>
      )}

      {currentStep === 2 && parent && (
        <>
          <div className={styles.creationSummary}>
            <div className={styles.summary}>
              <H4>{t(TRANSLATION_KEYS.summaryTitle)}</H4>
              <div className={styles.summaryItem}>
                <H5>{t(TRANSLATION_KEYS.location)}</H5>
                <M data-test="locationName">{getDisplayName(channel)}</M>
              </div>
              <div className={styles.summaryItem}>
                <H5>{t(TRANSLATION_KEYS.type)}</H5>
                <M data-test="channelType">{parent.type}</M>
              </div>
              <div className={styles.summaryItem}>
                <H5>{t(TRANSLATION_KEYS.parentChannel)}</H5>
                <M data-test="parentChannel">{getDisplayName(parent)}</M>
              </div>
              <div className={styles.summaryItem}>
                <H5>{t(TRANSLATION_KEYS.copiedFrom)}</H5>
                <M data-test="copiedFrom">{getDisplayName(copyFrom)}</M>
              </div>
              <S>{`*${t(TRANSLATION_KEYS.summaryNote)}`}</S>
            </div>
          </div>

          {newChannel && (
            <>
              <ToggleView
                title={t(TRANSLATION_KEYS.stepThreeEditNewChannelInfo)}
              >
                <ChannelInfoEdit
                  className={styles.panel}
                  channel={newChannel}
                  validation={null}
                  heading={t('web.admin.channel.new.heading', {
                    newChannel: getDisplayName(newChannel),
                  })}
                  onChannelUpdated={updateNewChannel}
                  onSuitesUpdate={(props: SuitesFields) => setSuiteIds(props)}
                  channelForDataIdentifiers={newChannel}
                  renderSuitSelector={
                    !!(
                      channel?.parent &&
                      channel.type === ActiveChannelTypeEnum.Property &&
                      channel.experienceType ===
                        ChannelExperienceTypeEnum.office
                    )
                  }
                />
              </ToggleView>
              <ToggleView
                title={t(TRANSLATION_KEYS.stepThreeEditNewChannelProfile)}
              >
                <ChannelProfileEdit
                  className={styles.panel}
                  channel={newChannel}
                  onChannelUpdated={updateNewChannel}
                />
              </ToggleView>
            </>
          )}

          <ControlMenu className={styles.menu}>
            <Button
              variant="outlined-light"
              disabled={!parent}
              onClick={() => setCurrentStep(1)}
              startIcon={<Icon name="chevron-left" />}
              loading={loading}
            >
              {t(TRANSLATION_KEYS.backButton)}
            </Button>
            <hr />
            <Button
              variant="contained"
              testId="createTenantButton"
              disabled={!parent}
              onClick={doCreate}
              loading={loading}
            >
              {t(TRANSLATION_KEYS.createButton)}
            </Button>
          </ControlMenu>
        </>
      )}
    </AdminPage>
  );
}
